// © Microsoft Corporation. All rights reserved.

/**
 * The app error object that contains the data of an app error
 */
export interface AppError {
    /** error id */
    id: number;

    /** source file */
    source?: string;

    /** Message or data */
    message?: string;

    /** severity */
    severity?: Severity;

    /** Property Bag */
    pb?: AppErrorPropertyBag;

    /** optional property: Bug meta data for auto bug creation from app error */
    bug?: any;
}

/**
 * The app error property bag that will be used for build number etc.
 */
export interface AppErrorPropertyBag {
    /** build number */
    build?: string;

    /** custom message */
    customMessage?: string;

    /** PCS build version */
    pcsVersion?: string;

    /** The error source. */
    source?: string;

    /** The line number of the error. */
    line?: number;

    /** The column number of the error. */
    column?: number;

    /** The stack trace for the error. */
    stack?: string;
}

/**
 * The interface that defines a generic app error tracker
 */
export interface AppErrorTracker {
    /**
     * Report an app error
     * @param {AppError} error - the app error to report
     */
    trackError(error: AppError): Promise<void>;
}

/**
 * This will decide whether to create Alert for this app error or no alert. This can generate IcM ticket.
 * Alerts ones are tracked at http://aka.ms/AppErrors
 * NoAlerts will have separate tab just next to Errors aka Warnings. IcM ticket won't be generated when NoAlert is set.
 */
export enum Severity {
    /**
     * This is app error with default severity
     */
    Alert,

    /**
     * This is app warning
     */
    NoAlert,

    /**
     * This app error has high user impact
     */
    HighImpact,

    /**
     * This is fatal app error
     */
    Critical
}